/**
 * Shared, isomorphic email validation for public-facing forms.
 *
 * Runs identically on the client (instant feedback) and on the server
 * (authoritative). Anything that reaches the email queue has already passed
 * this, so we do not burn send attempts on addresses that can never deliver.
 */

// Deliverability killers that a plain RFC regex happily accepts.
const OBVIOUS_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gnail.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmaill.com": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "iclould.com": "icloud.com",
  "icloud.co": "icloud.com",
};

const DISPOSABLE_DOMAINS = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  "temp-mail.org",
  "throwawaymail.com",
  "yopmail.com",
  "trashmail.com",
  "sharklasers.com",
  "getnada.com",
]);

// Deliberately conservative: one @, no spaces, a dotted TLD of 2+ chars.
const SHAPE = /^[^\s@,;:<>()[\]\\"]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

export interface EmailValidation {
  ok: boolean;
  /** Lowercased, trimmed address — use this everywhere downstream. */
  normalized: string;
  /** User-facing reason when `ok` is false. */
  error?: string;
  /** Non-blocking "did you mean" hint. */
  suggestion?: string;
}

export function validateEmail(raw: string): EmailValidation {
  const normalized = (raw ?? "").trim().toLowerCase();

  if (!normalized) return { ok: false, normalized, error: "Email is required." };
  if (normalized.length > 255) return { ok: false, normalized, error: "That email address is too long." };
  if (!SHAPE.test(normalized)) return { ok: false, normalized, error: "Enter a valid email address." };

  const domain = normalized.slice(normalized.lastIndexOf("@") + 1);
  const tld = domain.slice(domain.lastIndexOf(".") + 1);
  if (tld.length < 2) return { ok: false, normalized, error: "Enter a valid email address." };

  if (DISPOSABLE_DOMAINS.has(domain)) {
    return {
      ok: false,
      normalized,
      error: "Please use a permanent email address so we can send you licensing terms.",
    };
  }

  const corrected = OBVIOUS_TYPOS[domain];
  if (corrected) {
    const local = normalized.slice(0, normalized.lastIndexOf("@"));
    return {
      ok: false,
      normalized,
      error: `Did you mean ${local}@${corrected}?`,
      suggestion: `${local}@${corrected}`,
    };
  }

  return { ok: true, normalized };
}

/** Convenience for zod `.superRefine` / `.refine` chains. */
export function isValidEmail(raw: string): boolean {
  return validateEmail(raw).ok;
}
